/**
 * Users — Q Drives team management (Super Admin only).
 *
 * Mobile-first: stacked cards on small screens, dense table on desktop.
 * Capabilities:
 *   - List staff users (admin / executive / viewer)
 *   - Create new user (name + phone + role)
 *   - Toggle role / status (active|inactive) inline
 *   - View login history for any user
 *
 * Backend contract:
 *   GET    /admin/users
 *   POST   /admin/users               { name, phone, role }
 *   PATCH  /admin/users/{uid}         { name?, role?, status?, email? }
 *   GET    /admin/users/{uid}/login-history
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader, timeAgo } from "./_shared";
import { Plus, X, Clock } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { v: "admin",     l: "Admin",                 hint: "Full write + delete" },
  { v: "executive", l: "Acquisition Executive", hint: "Create/edit leads, update pricing, change stages, add notes" },
  { v: "viewer",    l: "Viewer",                hint: "Read-only access" },
];

const ROLE_COLOR = {
  admin: "#FF1E1E",
  executive: "#F5A623",
  viewer: "#6FA8DC",
};

export default function Users() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;

  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [historyForId, setHistoryForId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then(r => setItems(r.data)).catch(e => {
      toast.error("Failed to load users: " + (e?.response?.data?.detail || e.message));
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!isSuper) {
    return (
      <div data-testid="admin-users-forbidden" className="text-center py-16">
        <div className="qd-overline mb-2">Restricted</div>
        <div className="text-white/55 text-sm">User Management is Super Admin only.</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-users">
      <PageHeader
        title="Team & Access"
        subtitle={`${items.length} staff member${items.length === 1 ? "" : "s"}`}
        right={
          <button
            onClick={() => setShowNew(true)}
            data-testid="users-new-btn"
            className="btn-primary text-[11px] py-2 px-3 inline-flex items-center gap-1.5"
          >
            <Plus size={12} weight="bold" /> Add User
          </button>
        }
      />

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {items.map(u => (
          <UserCard key={u.id} u={u} onChange={load} onHistory={() => setHistoryForId(u.id)} />
        ))}
        {items.length === 0 && <EmptyState />}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-[#0E0E0E] border border-[#2A2A2A]">
        <table className="w-full font-mono-data text-xs">
          <thead>
            <tr className="text-left text-[10px] tracking-widest uppercase text-white/40 border-b border-[#2A2A2A]">
              <th className="p-3">Name</th>
              <th className="p-3">Mobile</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">Last Login</th>
              <th className="p-3">Created</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(u => <UserRow key={u.id} u={u} onChange={load} onHistory={() => setHistoryForId(u.id)} />)}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-white/40 py-8">No staff users yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && <NewUserDialog onClose={() => setShowNew(false)} onCreated={() => { load(); setShowNew(false); }} />}
      {historyForId && <LoginHistoryDialog uid={historyForId} onClose={() => setHistoryForId(null)} />}

      {/* ============ WhatsApp Integration Smoke Test (Super Admin) ============ */}
      <WhatsAppTestPanel />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-[#0E0E0E] border border-[#2A2A2A] py-10 text-center text-white/40 text-sm">
      No staff users yet. Tap “Add User” to invite your first executive.
    </div>
  );
}

function UserRow({ u, onChange, onHistory }) {
  const canEdit = !u.is_super_admin;
  return (
    <tr className="border-b border-[#1A1A1A] hover:bg-[#121212]">
      <td className="p-3 text-white">
        <div className="font-bold">{u.name || "—"}</div>
        {u.email && <div className="text-[10px] text-white/40">{u.email}</div>}
      </td>
      <td className="p-3 text-white/70">{u.phone}</td>
      <td className="p-3">
        {u.is_super_admin ? (
          <RolePill role="super_admin" />
        ) : (
          <RoleSelect uid={u.id} value={u.role} onChange={onChange} disabled={!canEdit} />
        )}
      </td>
      <td className="p-3">
        <StatusToggle uid={u.id} status={u.status || "active"} onChange={onChange} disabled={!canEdit} />
      </td>
      <td className="p-3 text-white/55">{u.last_login_at ? `${timeAgo(u.last_login_at)} ago` : "Never"}</td>
      <td className="p-3 text-white/55">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—"}</td>
      <td className="p-3 text-right">
        <button
          onClick={onHistory}
          data-testid={`user-history-${u.id}`}
          className="btn-secondary text-[10px] py-1 px-2 inline-flex items-center gap-1"
        >
          <Clock size={11} weight="bold" /> History
        </button>
      </td>
    </tr>
  );
}

function UserCard({ u, onChange, onHistory }) {
  const canEdit = !u.is_super_admin;
  return (
    <div data-testid={`user-card-${u.id}`} className="bg-[#0E0E0E] border border-[#2A2A2A] p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-white font-display text-[15px] truncate">{u.name || "—"}</div>
          <div className="text-[10px] font-mono-data text-white/55">{u.phone}</div>
          {u.email && <div className="text-[10px] text-white/40 truncate">{u.email}</div>}
        </div>
        {u.is_super_admin && <RolePill role="super_admin" />}
      </div>
      {!u.is_super_admin && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">Role</div>
            <RoleSelect uid={u.id} value={u.role} onChange={onChange} disabled={!canEdit} variant="mobile" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">Status</div>
            <StatusToggle uid={u.id} status={u.status || "active"} onChange={onChange} disabled={!canEdit} variant="mobile" />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-white/45">
        <div className="flex flex-col">
          <span>Last login · {u.last_login_at ? `${timeAgo(u.last_login_at)} ago` : "Never"}</span>
          {u.created_at && <span className="text-white/30">Joined · {new Date(u.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</span>}
        </div>
        <button
          onClick={onHistory}
          data-testid={`user-history-mobile-${u.id}`}
          className="text-[#FF1E1E] inline-flex items-center gap-1 hover:underline"
        >
          <Clock size={10} weight="bold" /> History
        </button>
      </div>
    </div>
  );
}

function RolePill({ role }) {
  if (role === "super_admin") {
    return (
      <span className="text-[9px] tracking-widest uppercase font-bold px-2 py-1 bg-[#FF1E1E]/15 text-[#FF1E1E] border border-[#FF1E1E]/40">
        Super Admin
      </span>
    );
  }
  return null;
}

function RoleSelect({ uid, value, onChange, disabled, variant = "desktop" }) {
  const save = async (next) => {
    if (next === value) return;
    try {
      await api.patch(`/admin/users/${uid}`, { role: next });
      toast.success(`Role → ${next}`);
      onChange();
    } catch (e) { toast.error("Update failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const testid = variant === "mobile" ? `user-role-mobile-${uid}` : `user-role-${uid}`;
  return (
    <select
      data-testid={testid}
      value={value}
      disabled={disabled}
      onChange={(e) => save(e.target.value)}
      className="w-full bg-[#0F0F0F] border border-[#2A2A2A] text-[11px] tracking-wider uppercase px-2 py-1.5 font-mono-data focus:outline-none focus:border-[#FF1E1E] disabled:opacity-50"
      style={{ color: ROLE_COLOR[value] || "#fff" }}
    >
      {ROLE_OPTIONS.map(o => <option key={o.v} value={o.v} style={{ color: "#fff", background: "#0F0F0F" }}>{o.l}</option>)}
    </select>
  );
}

function StatusToggle({ uid, status, onChange, disabled, variant = "desktop" }) {
  const active = status === "active";
  const toggle = async () => {
    const next = active ? "inactive" : "active";
    try {
      await api.patch(`/admin/users/${uid}`, { status: next });
      toast.success(`Status → ${next}`);
      onChange();
    } catch (e) { toast.error("Update failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const testid = variant === "mobile" ? `user-status-mobile-${uid}` : `user-status-${uid}`;
  return (
    <button
      onClick={toggle}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-[10px] tracking-widest uppercase font-bold px-2 py-1.5 border transition-colors ${
        active
          ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/15"
          : "border-white/15 text-white/40 bg-white/5 hover:bg-white/10"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {active ? "Active" : "Inactive"}
    </button>
  );
}

function NewUserDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("executive");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", { name: name.trim(), phone: phone.trim(), role });
      toast.success("User created");
      onCreated();
    } catch (e) {
      toast.error("Create failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="new-user-dialog" className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit} className="bg-[#0E0E0E] border border-[#2A2A2A] w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="qd-h1 text-lg text-white">Invite Team Member</h2>
          <button type="button" onClick={onClose} className="text-white/55 hover:text-white" data-testid="new-user-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Name</label>
          <input
            data-testid="new-user-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Ravi Kumar"
            className="qd-input-block w-full"
            autoFocus
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Mobile (10-digit)</label>
          <input
            data-testid="new-user-phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="9XXXXXXXXX"
            inputMode="numeric"
            className="qd-input-block w-full font-mono-data"
          />
          <div className="text-[10px] text-white/40 mt-1">They&apos;ll log in with this number + OTP.</div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Role</label>
          <div className="space-y-1.5">
            {ROLE_OPTIONS.map(o => (
              <label
                key={o.v}
                data-testid={`new-user-role-${o.v}`}
                className={`flex items-center gap-3 p-2.5 border cursor-pointer ${
                  role === o.v ? "border-[#FF1E1E] bg-[#FF1E1E]/5" : "border-[#2A2A2A] hover:border-white/20"
                }`}
              >
                <input
                  type="radio"
                  name="user-role"
                  checked={role === o.v}
                  onChange={() => setRole(o.v)}
                  className="accent-[#FF1E1E]"
                />
                <div className="flex-1">
                  <div className="text-white text-sm">{o.l}</div>
                  <div className="text-[10px] text-white/45">{o.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs py-2 px-4 flex-1"
            data-testid="new-user-cancel"
          >Cancel</button>
          <button
            type="submit"
            disabled={saving}
            data-testid="new-user-submit"
            className="btn-primary text-xs py-2 px-4 flex-1 disabled:opacity-50"
          >{saving ? "Creating…" : "Create User"}</button>
        </div>
      </form>
    </div>
  );
}

function LoginHistoryDialog({ uid, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${uid}/login-history`)
      .then(r => setItems(r.data))
      .catch(() => setItems([]));
  }, [uid]);

  return (
    <div data-testid="user-history-dialog" className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-3">
      <div className="bg-[#0E0E0E] border border-[#2A2A2A] w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="qd-h1 text-lg text-white">Login History</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white" data-testid="user-history-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        {items === null && <div className="text-white/40 text-sm py-4">Loading…</div>}
        {items && items.length === 0 && <div className="text-white/40 text-sm py-4">No login activity yet.</div>}
        {items && items.length > 0 && (
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {items.map((it, i) => (
              <div key={i} className="border-l-2 border-[#2A2A2A] pl-3 py-1.5">
                <div className="text-[10px] font-mono-data tracking-widest text-white/40 uppercase">
                  {it.extra?.method || "login"} · {timeAgo(it.created_at)} ago
                </div>
                <div className="text-xs text-white/65 font-mono-data">{new Date(it.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ===================================================================
// WhatsAppTestPanel — Super Admin smoke test for the Gupshup integration.
// Sends the approved `purchase_offer` (or `updated_purchase_offer`) template
// to an arbitrary phone using a synthetic vehicle payload, so you can verify
// delivery before using live leads.
// ===================================================================
function WhatsAppTestPanel() {
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [kind, setKind] = useState("initial");
  const [amount, setAmount] = useState("1450000");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadStatus = () => api.get("/admin/whatsapp/status").then(r => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  const loadHistory = () => api.get("/admin/whatsapp/test-history?limit=10").then(r => setHistory(r.data?.items || [])).catch(() => setHistory([]));

  useEffect(() => { loadStatus(); loadHistory(); }, []);

  // Auto-poll history for 30s after a send so the operator sees status flips.
  useEffect(() => {
    if (!history.length) return;
    const last = history[0];
    if (!last || ["read", "failed", "delivered"].includes(last.status)) return;
    const t = setInterval(loadHistory, 5000);
    return () => clearInterval(t);
  }, [history]);

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length < 10) return toast.error("Enter a valid mobile number");
    const amt = parseInt(amount, 10) || 0;
    if (amt <= 0) return toast.error("Enter a valid offer amount");
    setSending(true);
    try {
      const r = await api.post("/admin/whatsapp/test-send", {
        phone: cleaned.length === 10 ? `91${cleaned}` : cleaned,
        template_kind: kind,
        offer_price: amt,
      });
      if (r.data?.status === "failed") {
        toast.error("Test failed: " + (r.data.failure_reason || "unknown"));
      } else {
        toast.success("WhatsApp purchase offer sent successfully.");
      }
      loadHistory();
    } catch (err) {
      toast.error("Send failed: " + (err?.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  const STATUS_COLOR = {
    pending:   { fg: "#FFFFFF", bg: "rgba(255,255,255,0.10)" },
    submitted: { fg: "#F5A623", bg: "rgba(245,166,35,0.15)" },
    enqueued:  { fg: "#F5A623", bg: "rgba(245,166,35,0.15)" },
    sent:      { fg: "#F5A623", bg: "rgba(245,166,35,0.20)" },
    delivered: { fg: "#6FA8DC", bg: "rgba(111,168,220,0.15)" },
    read:      { fg: "#34D399", bg: "rgba(52,211,153,0.15)" },
    failed:    { fg: "#FF1E1E", bg: "rgba(255,30,30,0.15)" },
  };

  return (
    <section data-testid="whatsapp-test-panel" className="mt-8 bg-[#0E0E0E] border border-[#2A2A2A] p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="qd-h1 text-base text-white tracking-wider uppercase">WhatsApp Integration Test</h3>
          <p className="text-[11px] text-white/45 mt-0.5">
            Sends the approved <span className="font-mono-data text-white/70">purchase_offer</span> template via Gupshup to a phone of your choice. Use this before going live on real leads.
          </p>
        </div>
        <ConfigBadge status={status} />
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_120px] gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Recipient Mobile</label>
          <input
            data-testid="wa-test-phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="9XXXXXXXXX (10-digit Indian) or +91…"
            inputMode="numeric"
            className="qd-input-block w-full font-mono-data"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Template</label>
          <select
            data-testid="wa-test-kind"
            value={kind}
            onChange={e => setKind(e.target.value)}
            className="qd-input-block w-full font-mono-data text-xs"
          >
            <option value="initial">purchase_offer</option>
            <option value="updated">updated_purchase_offer</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Offer ₹</label>
          <input
            data-testid="wa-test-amount"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/\D/g, ""))}
            className="qd-input-block w-full font-mono-data"
          />
        </div>
        <button
          type="submit"
          disabled={sending || !status?.configured}
          data-testid="wa-test-submit"
          className="btn-primary text-[11px] py-2 px-3 inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? "Sending…" : "Send Test"}
        </button>
      </form>

      <p className="text-[10px] text-white/35 mt-2">
        Sample payload: Hyundai · Creta · SX Turbo DCT · TS 09 EX 1234 · Petrol · Automatic · 2023 · 22,500 km · ₹<span className="font-mono-data">{parseInt(amount, 10).toLocaleString("en-IN")}</span>.
      </p>

      {/* Per-variable presence diagnostic — pinpoints which env var is missing
          in the running container. Useful when Send Offer and Test panel
          appear to disagree (they cannot — same process — but if you ever
          suspect a deploy-config drift, look here first). */}
      <EnvVarMap status={status} />

      {/* Recent test sends */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <h4 className="text-[10px] tracking-widest uppercase text-white/45">Recent Tests · {history.length}</h4>
          <button onClick={loadHistory} className="btn-secondary text-[10px] py-1 px-2" data-testid="wa-test-refresh">Refresh</button>
        </div>
        {history.length === 0
          ? <div className="text-xs text-white/35 py-2">No test sends yet.</div>
          : (
            <div className="space-y-1.5">
              {history.map(t => {
                const c = STATUS_COLOR[t.status] || STATUS_COLOR.pending;
                return (
                  <div
                    key={t.id}
                    data-testid={`wa-test-row-${t.id}`}
                    className="grid grid-cols-[1fr_auto] items-center gap-2 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#1F1F1F]"
                  >
                    <div className="min-w-0 text-[11px]">
                      <span className="font-mono-data text-white/85">+{(t.destination || "").replace(/^\+?/, "")}</span>
                      <span className="text-white/30 mx-1.5">·</span>
                      <span className="text-white/65 font-mono-data">{t.template_name}</span>
                      <span className="text-white/30 mx-1.5">·</span>
                      <span className="text-white/85">₹{(t.amount || 0).toLocaleString("en-IN")}</span>
                      {t.message_id && (
                        <div className="text-[9px] font-mono-data text-white/30 truncate" title={t.message_id}>id: {t.message_id}</div>
                      )}
                      {t.status === "failed" && t.failure_reason && (
                        <div className="text-[10px] text-[#FF1E1E]/85 truncate" title={t.failure_reason}>{t.failure_reason}</div>
                      )}
                      <div className="text-[9px] text-white/30">{t.sent_at ? new Date(t.sent_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}</div>
                    </div>
                    <span
                      className="text-[9px] tracking-widest uppercase font-bold px-2 py-1 border whitespace-nowrap"
                      style={{ color: c.fg, background: c.bg, borderColor: c.fg + "55" }}
                    >
                      {t.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </section>
  );
}

function ConfigBadge({ status }) {
  if (!status) return null;
  const ok = status.configured;
  return (
    <div className="text-right shrink-0" data-testid="wa-config-badge">
      <span
        className="inline-block text-[9px] tracking-widest font-bold uppercase px-2 py-1 border"
        style={ok
          ? { color: "#34D399", background: "rgba(52,211,153,0.10)", borderColor: "#34D39955" }
          : { color: "#FF1E1E", background: "rgba(255,30,30,0.10)", borderColor: "#FF1E1E55" }}
      >
        {ok ? "Configured" : "Not Configured"}
      </span>
      {ok && status.sender && (
        <div className="text-[9px] font-mono-data text-white/40 mt-1">src: +{status.sender}</div>
      )}
      {status.host && (
        <div className="text-[9px] font-mono-data text-white/30 mt-0.5" title="Container hostname">
          host: {status.host.slice(0, 14)}…
        </div>
      )}
      {status.env && (
        <div className="text-[9px] font-mono-data text-white/30">env: {status.env}</div>
      )}
    </div>
  );
}

// Env-var presence map — shows operators which exact variable is missing
// in the running container, with no value leakage.
function EnvVarMap({ status }) {
  if (!status?.env_vars) return null;
  const ORDER = [
    "GUPSHUP_API_KEY",
    "GUPSHUP_APP_NAME",
    "GUPSHUP_SENDER_NUMBER",
    "GUPSHUP_TEMPLATE_PURCHASE_OFFER_ID",
    "GUPSHUP_TEMPLATE_UPDATED_PURCHASE_OFFER_ID",
  ];
  const REQUIRED = new Set(["GUPSHUP_API_KEY", "GUPSHUP_APP_NAME", "GUPSHUP_SENDER_NUMBER"]);
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="wa-env-var-map">
      {ORDER.map(k => {
        const v = status.env_vars[k] || { present: false };
        const required = REQUIRED.has(k);
        const ok = !!v.present;
        return (
          <div key={k} className="flex items-center justify-between px-2.5 py-1.5 bg-[#0A0A0A] border border-[#1F1F1F]">
            <div className="min-w-0">
              <div className="font-mono-data text-[10px] text-white/70 truncate">{k}</div>
              {!required && <div className="text-[9px] text-white/30">optional</div>}
              {ok && v.prefix && <div className="text-[9px] font-mono-data text-white/40">value: {v.prefix}</div>}
            </div>
            <span
              className="text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 border"
              style={ok
                ? { color: "#34D399", background: "rgba(52,211,153,0.10)", borderColor: "#34D39955" }
                : { color: required ? "#FF1E1E" : "#888", background: required ? "rgba(255,30,30,0.10)" : "rgba(255,255,255,0.05)", borderColor: (required ? "#FF1E1E" : "#888") + "55" }}
            >
              {ok ? "Set" : required ? "Missing" : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
